<template>
  <div>
    <v-dialog
      v-model="dialog"
      width="70vw"
      height="80vh"
    >
      <v-card>
        <v-card-actions class="close-button">
          <DsButton
            v-tooltip="closeLabel"
            color="tertiary"
            name="close"
            :icon="true"
            @click="dialog = false"
          />
        </v-card-actions>
        <v-card-text class="modal-style">
          <h3>{{ this.$t('components.GPTSuggestions.faq.question') }}</h3>
          <p>{{ this.$t('components.GPTSuggestions.faq.response') }}</p>
        </v-card-text>
      </v-card>
    </v-dialog>

    <template v-for="(modal, index) in cardOptionsModals">
      <DynamicModalComponent
        v-if="modal.props.value"
        :key="index"
        :component-name="modal.componentName"
        :custom-props="modal.props"
        :events="modal.events"
      />
    </template>

    <GenericChart
      :request-status="requestStatus"
      :item-id="itemId"
      :title="title"
      :has-custom-filter="true"
      :has-data-to-export="false"
      :open-filter="openGenericFilterModal"
      :open-dialog="openDialog"
      v-bind="$attrs"
      @reload="getData"
    />

    <StLoader
      v-if="inputLoading"
      :loading="true"
    />

    <div
      v-if="!inputLoading"
      class="gpt-suggestions"
    >
      <div class="gpt-suggestions__carousel">
        <v-carousel
          v-if="hasPosts"
          v-model="postIndex"
          :dark="$vuetify.theme.dark"
          :light="!$vuetify.theme.dark"
          hide-delimiters
          :show-arrows-on-hover="true"
          :continuous="false"
        >
          <v-carousel-item
            v-for="(post,index) in posts"
            :key="index"
          >
            <div class="gpt-suggestions__posts">
              <Post
                :post="post"
                :include-post-order="true"
                @order-posts="handleOrderPosts"
              />
            </div>
          </v-carousel-item>
        </v-carousel>

        <div
          v-else
          class="gpt-suggestions__no-data"
        >
          <h2>
            {{ $t('components.GPTSuggestions.label.status.withoutData') }}
          </h2>
        </div>
      </div>

      <div class="gpt-suggestions__divider" />

      <div class="gpt-suggestions__form">
        <div>
          <p class="gpt-suggestions__labels mb-5">
            {{ $t('components.GPTSuggestions.label.pageSession') }}
          </p>

          <SingleSelect
            v-model="pageSelected"
            :searchable="true"
            :items="formattedPages"
            :label="$t('components.GPTSuggestions.label.pageLabel')"
            :disabled="getPostChannel() !== twitterString"
          >
            <template v-slot:selection="{item}">
              <span>
                {{ item.text }}
              </span>
            </template>
          </SingleSelect>
        </div>

        <p class="gpt-suggestions__labels">
          {{ $t('components.GPTSuggestions.label.suggestionSession') }}
        </p>

        <div class="gpt-suggestions__suggestions">
          <v-list>
            <v-list-item-group
              v-model="selectedSuggestion"
            >
              <v-list-item
                v-for="(suggestion, index) in suggestions"
                :key="`item-${index}`"
              >
                <template v-slot:default>
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ suggestion }}
                    </v-list-item-title>
                  </v-list-item-content>
                </template>
              </v-list-item>
            </v-list-item-group>
          </v-list>

          <StLoader
            v-if="suggestionsRequestStatus === 'loading'"
            :loading="true"
          />

          <div
            v-if="suggestionsRequestStatus === 'error'"
          >
            <h4 class="ml-2">
              {{ $t('components.GPTSuggestions.label.status.errorData') }}
            </h4>
          </div>
        </div>

        <div class="gpt-suggestions__suggestion-button">
          <DsButton
            color="secondary"
            size="dense"
            :disabled="!hasPosts"
            @click="getGptSuggestions"
          >
            {{ $t('components.GPTSuggestions.label.button.generateSuggestions') }}
          </DsButton>
        </div>

        <p class="gpt-suggestions__labels">
          {{ $t('components.GPTSuggestions.label.replySession') }}
        </p>

        <div class="gpt-suggestions__selected">
          <v-textarea
            v-model="answer"
            :label="$t('components.GPTSuggestions.label.replyLabel', {username: getUsername})"
            :rules="ruleValidation"
            solo
            counter
            auto-grow
          />
        </div>

        <div
          class="gpt-suggestions__button-container"
        >
          <DsButton
            class="gpt-suggestions__remove-button"
            color="secondary"
            size="dense"
            @click="remove"
          >
            {{ $t('components.GPTSuggestions.label.button.remove') }}
          </DsButton>
          <DsButton
            v-if="getPostChannel() === twitterString"
            class="gpt-suggestions__reply-button"
            color="primary"
            size="dense"
            :disabled="!canReply"
            @click="reply"
          >
            {{ $t('components.GPTSuggestions.label.button.reply') }}
          </DsButton>
          <CopyToClipboard
            v-else
            class="gpt-suggestions__reply-button"
            :content="answer"
            :is-button="true"
            :disabled="!canCopyToClipboard"
            @click="reply"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import GenericChart from '@/components/cards/models/GenericChart';
import cardsModel from '@/mixins/cards-model';
import filtersModel from '@/mixins/filters-model';
import EventBus from '@/utils/event-bus';
import Post from '@/components/Post/Post';
import { DsButton } from '@ds/components';
import { mapActions, mapGetters } from 'vuex';
import StLoader from '@stilingue/st-loader';
import DynamicModalComponent from '@/components/modal-model/DynamicModalComponent';
import CopyToClipboard from '@/components/CopyToClipboard';
import SingleSelect from '@/components/filter/models/SingleSelect';

export default {
  name: 'GPTSuggestions',
  components: {
    GenericChart,
    DsButton,
    Post,
    StLoader,
    DynamicModalComponent,
    CopyToClipboard,
    SingleSelect,
  },
  mixins: [
    cardsModel,
    filtersModel,
  ],
  props: {
    title: {
      type: String,
      default: '',
    },
    itemId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      posts: [],
      postIndex: 0,
      suggestions: [],
      answer: undefined,
      selectedSuggestion: undefined,
      suggestionsRequestStatus: 'success',
      requestStatus: 'loading',
      pageSelected: undefined,
      orderType: 'date_desc',
      postsLimit: 50,
      minChar: 20,
      maxChar: 280,
      replyRules: [
        v => (Boolean(v !== undefined) && Boolean(v.length >= this.minChar)) || this.$t('components.GPTSuggestions.label.rules.minChar', { minChar: this.minChar }),
        v => (Boolean(v !== undefined) && Boolean(v.length <= this.maxChar)) || this.$t('components.GPTSuggestions.label.rules.maxChar', { maxChar: this.maxChar }),
      ],
      cardModalMethods: {
        openGenericFilterModal: this.openGenericFilterModal,
      },
      twitterString: 'Twitter',
      dialog: false,
      closeLabel: this.$t('components.GPTSuggestions.label.closeLabel'),
    };
  },
  computed: {
    ...mapGetters('cards', [
      'cards',
    ]),
    ...mapGetters('dashboard', [
      'activeDashboard',
    ]),
    ...mapGetters('user', [
      'user',
    ]),
    hasPosts() {
      return this.posts && this.posts.length;
    },
    getPid() {
      return this.hasPosts ? this.posts[this.postIndex].pid : '';
    },
    getUsername() {
      return this.hasPosts ? this.posts[this.postIndex].username : '';
    },
    formattedPages() {
      return this.activeDashboard?.data?.pages.filter(page => page.channel === 'twitter').map(page => {
        return {
          value: page.id,
          text: page.username,
        };
      });
    },
    getUser() {
      return this.user;
    },
    hasMorePosts() {
      return Boolean((this.postIndex <= this.postsLimit) && (this.postIndex < this.posts.length));
    },
    hasAnswer() {
      return Boolean(this.answer !== undefined && this.answer !== '');
    },
    hasSelectedPage() {
      return Boolean(this.pageSelected !== undefined);
    },
    canReply() {
      return this.hasAnswer && this.hasSelectedPage && this.replyRules;
    },
    canCopyToClipboard() {
      return this.hasAnswer && this.replyRules;
    },
    inputLoading() {
      return this.requestStatus === 'loading';
    },
    ruleValidation() {
      return this.answer ? this.replyRules : [];
    },
  },
  watch: {
    postIndex(newValue, oldValue) {
      if (newValue === oldValue) {
        return;
      }

      this.updatePageSelected(newValue);

      this.suggestions = [];
      this.answer = undefined;
    },
    selectedSuggestion(newValue, oldValue) {
      if (newValue === oldValue) {
        return;
      }
      if (this.suggestions.length) {
        this.answer = this.suggestions[newValue];
      }
    },

  },
  created() {
    this.getData();
  },
  methods: {
    ...mapActions('cards', [
      'getWarroomService',
      'getSeStudioEndpointService',
    ]),
    updatePageSelected(postIndex) {
      if (this.posts.length > 0 && this.posts[postIndex].channel === this.twitterString) {
        this.pageSelected = this.formattedPages[0];
      } else {
        this.pageSelected = undefined;
      }
    },
    getData(options) {
      if (!options?.silentRefresh) {
        this.requestStatus = 'loading';
      }

      this.applyFilter(options);
    },
    handleOrderPosts(orderType) {
      this.orderType = orderType;
      this.applyFilter();
    },
    async applyFilter(options) {
      this.postIndex = 0;
      this.pageSelected = undefined;
      this.getWarroomService({
        itemId: this.itemId,
        servicePath: 'topPosts',
        serviceParams: {
          limit: this.postsLimit,
          order_by: this.orderType,
        },
      })
        .then((response) => {
          this.posts = response.posts;
          this.updatePageSelected(0);
          if (options?.silentRefresh) {
            EventBus.$emit('updated', { itemId: this.itemId });
          }
          this.requestStatus = 'success';
        })
        .catch((error) => {
          console.error(error);
          this.requestStatus = 'error';
        });
    },
    async getGptSuggestions() {
      this.suggestions = [];
      this.suggestionsRequestStatus = 'loading';

      this.getSeStudioEndpointService({
        itemId: this.itemId,
        servicePath: 'gptSuggestion',
        serviceParams: {
          postId: this.getPid,
        },
      }).then((response) => {
        this.suggestionsRequestStatus = 'success';

        this.suggestions = response.messages;
      }).catch((error) => {
        console.error(error);

        this.suggestionsRequestStatus = 'error';
      });
    },
    async reply() {
      const postChannel = this.getPostChannel();
      const pageId = this.pageSelected ? this.pageSelected.value : undefined;
      this.getSeStudioEndpointService({
        itemId: this.itemId,
        servicePath: 'reply',
        serviceParams: {
          pid: this.getPid,
          pageId,
          tweet: this.answer,
          user: { ...this.getUser },
          channel: postChannel,
        },
      }).then(() => {
        if (this.hasMorePosts) {
          this.postIndex++;
        }

        this.$noty.success(this.$t('components.GPTSuggestions.notification.success.reply'));
      }).catch((error) => {
        console.error(error);
        this.$noty.error(this.$t('components.GPTSuggestions.notification.error.reply'));
      });
    },
    async remove() {
      const postChannel = this.getPostChannel();
      const pageId = this.pageSelected ? this.pageSelected.value : undefined;
      this.getSeStudioEndpointService({
        itemId: this.itemId,
        servicePath: 'remove',
        serviceParams: {
          pid: this.getPid,
          pageId,
          user: { ...this.getUser },
          channel: postChannel,
        },
      }).then(() => {
        if (this.hasMorePosts) {
          this.postIndex++;
        }

        this.$noty.success(this.$t('components.GPTSuggestions.notification.success.remove'));
      }).catch((error) => {
        console.error(error);
        this.$noty.error(this.$t('components.GPTSuggestions.notification.error.remove'));
      });
    },
    getPostChannel() {
      return this.hasPosts ? this.posts[this.postIndex].channel : '';
    },
    openDialog() {
      this.dialog = true;
    },
  },
};
</script>

<style scoped lang="scss">
.close-button {
  display: flex;
  flex: end;
  justify-content: flex-end;
}

.gpt-suggestions {
  display: flex;
  flex-flow: space-between;

  &__labels {
    color: var(--ds-color-text-1);
    font-size: var(--ds-font-size-sm);
    line-height: var(var(--ds-font-line-height-tight));
    font-weight: var(--ds-font-weight-semibold);
    margin-bottom: var(--ds-spacing-inline-nano);
  }

  &__carousel {
    min-width: 48%;
  }

  &__posts {
    width: 80%;
    height: 100%;
    margin-left: 44px;
    overflow-y: overlay;
  }

  &__no-data {
    display: flex;
    height: 100%;
    justify-content: center;
    align-items: center;
  }

  &__divider {
    height: auto;
    margin-right: 12px;
    border: var(--ds-border-width-hairline) solid var(--ds-color-overlay);
  }

  &__form {
    min-width: 48%;
  }

  &__suggestions,
  &__selected {
    border: var(--ds-border-width-hairline) solid var(--ds-color-overlay);
    border-radius: var(--ds-border-radius-md);
    min-height: 150px;
  }

  &__suggestion-button {
    display: flex;
    justify-content: flex-end;
    margin: var(--ds-spacing-inline-nano) var(--ds-spacing-stack-quark) var(--ds-spacing-inline-md) 0;
  }

  &__button-container {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    margin: var(--ds-spacing-inline-nano) var(--ds-spacing-stack-quark) var(--ds-spacing-inline-md) 0;
  }

  &__reply-button,
  &__remove-button {
    display: inline-block;
    margin-right: var(--ds-spacing-stack-quark);
    vertical-align: middle;
  }
}

::v-deep {
  .v-list-item__title,
  .v-list-item__subtitle {
    text-overflow: inherit !important;
    white-space: inherit !important;
  }

  .st-vue-loader {
    position: relative;
    margin-top: 15%;
  }

  .gpt-suggestions__selected {
    .v-text-field__slot {
      background-color: var(--ds-color-overlay);
      margin-right: 0 !important;
      border-radius: 4px;

      .v-label,
      textarea {
        margin-left: 8px;
      }
    }
  }

  .v-carousel__item,
  .v-item-group {
    height: auto !important;
  }

  .v-window__prev,
  .v-window__next {
    top: 250px;
  }

  .modal-style {
    * {
      color: var(--ds-color-snow) !important;
    }

    h3 {
      font-weight: var(--ds-font-weight-bold);
    }
  }

  .v-dialog {
    overflow-x: hidden;
  }
}

</style>
